import { normaliseTheme } from './ai-suggestions.ts'
import { normaliseThemeLabel, THEMES, themeLabel, themeSlug } from './themes.ts'

/**
 * Song themes (docs/15-MUSIC-AND-AI.md, "Song themes from the shared
 * catalog"). A recording in the shared catalog has its themes, given once by
 * AI from the vocabulary and kept up by what churches do with them; a
 * church's library song wears its recording's themes, plus the ones the
 * church added, minus the ones it removed. The church's adds and removes are
 * what is stored — never a copy — so a change to the catalog reaches every
 * church at once. A song with no recording (not on Apple Music, or not
 * matched yet) wears its own AI themes instead, with the church's changes on
 * top the same way.
 *
 * A stored theme is a vocabulary slug (`resurrection`) or, for a church's own
 * theme outside the vocabulary (from before, or an import), its label as the
 * library keeps it ("Hymn").
 */

// ---- one theme, as stored and as read ------------------------------------------------------

/** What a church's song keeps for a theme: `ai` (its own AI themes), `added`, `removed`. */
export const SONG_THEME_KINDS = ['ai', 'added', 'removed'] as const
export type SongThemeKind = (typeof SONG_THEME_KINDS)[number]

/** A theme as stored: the vocabulary's slug where it maps, else the label tidied; null for nothing. */
export function canonicalTheme(label: string): string | null {
  const slug = themeSlug(label)
  if (slug) return slug
  return normaliseTheme(label) || null
}

/** A stored theme as people read it: the vocabulary's label, or the church's own. */
export const themeDisplay = (value: string): string => themeLabel(value) ?? value

const vocabularyOrder = new Map(THEMES.map((entry, index) => [entry.slug, index] as const))

/**
 * Themes in the order a song shows them: the vocabulary's in its order (the
 * church year first), then the church's own, alphabetically. Nothing twice.
 */
export function orderThemes(values: Iterable<string>): string[] {
  const unique = [...new Set(values)]
  return unique.sort((a, b) => {
    const ia = vocabularyOrder.get(a)
    const ib = vocabularyOrder.get(b)
    if (ia !== undefined && ib !== undefined) return ia - ib
    if (ia !== undefined) return -1
    if (ib !== undefined) return 1
    return a.localeCompare(b, 'en', { sensitivity: 'base' })
  })
}

/** Canonical values, in order, without the ones that are nothing. */
export function canonicalThemes(labels: readonly string[]): string[] {
  return orderThemes(labels.flatMap((label) => canonicalTheme(label) ?? []))
}

// ---- a library song's themes -----------------------------------------------------------------

/** A church's own choices for one song. */
export interface SongThemeChoices {
  added: readonly string[]
  removed: readonly string[]
}

/**
 * A song's themes as everyone sees them: its base (the recording's catalog
 * themes when it has one, else its own AI themes) without the church's
 * removed ones, with its added ones.
 */
export function effectiveThemes(base: readonly string[], choices: SongThemeChoices): string[] {
  const removed = new Set(choices.removed)
  return orderThemes([...base.filter((t) => !removed.has(t)), ...choices.added])
}

/** Where a song's theme comes from, for its editor. */
export type SongThemeOrigin = 'catalog' | 'ai' | 'church'

export interface SongThemeView {
  /** The stored value: a slug, or the church's own label. */
  value: string
  label: string
  origin: SongThemeOrigin
}

/**
 * A song's themes with where each comes from: the shared catalog (its
 * recording), its own AI themes, or the church.
 */
export function songThemeViews(input: {
  /** The recording's catalog themes; null when it has no recording. */
  catalog: readonly string[] | null
  /** Its own AI themes (used only without a recording). */
  ai: readonly string[]
  choices: SongThemeChoices
}): SongThemeView[] {
  const base = input.catalog ?? input.ai
  const baseOrigin: SongThemeOrigin = input.catalog ? 'catalog' : 'ai'
  const inBase = new Set(base)
  return effectiveThemes(base, input.choices).map((value) => ({
    value,
    label: themeDisplay(value),
    origin: inBase.has(value) ? baseOrigin : 'church',
  }))
}

/**
 * The church's choices that give a song the themes it wants: what the base
 * lacks is added, what the base has and is not wanted is removed. An add the
 * church made that the base has since gained is kept (it said so; should the
 * catalog drop it, the song keeps it).
 */
export function themeChoicesFor(
  base: readonly string[],
  current: SongThemeChoices,
  wanted: readonly string[],
): { added: string[]; removed: string[] } {
  const want = new Set(wanted)
  const inBase = new Set(base)
  const added = orderThemes([
    ...[...want].filter((t) => !inBase.has(t)),
    ...current.added.filter((t) => want.has(t) && inBase.has(t)),
  ])
  const removed = orderThemes(base.filter((t) => !want.has(t)))
  return { added, removed }
}

/**
 * What a song's own AI themes were given for: its title and artist as
 * compared (case, accents and punctuation aside). Renamed past this, it is
 * given them again.
 */
export function songThemesKey(title: string, artist: string | null): string {
  return `${normaliseThemeLabel(title)}|${normaliseThemeLabel(artist ?? '')}`
}

// ---- the catalog's themes: what churches agree on -------------------------------------------

/** A theme churches added becomes the recording's once this many churches added it. */
export const CATALOG_THEME_ADD_CHURCHES = 3
/** A catalog theme goes once at least this many churches removed it… */
export const CATALOG_THEME_DROP_CHURCHES = 3
/** …and they are at least this share of the churches that have the recording in their library. */
export const CATALOG_THEME_DROP_SHARE = 0.5

/** A platform operator's decision on one recording's theme: it overrides the churches. */
export const CATALOG_THEME_OPERATOR = ['pinned', 'blocked'] as const
export type CatalogThemeOperator = (typeof CATALOG_THEME_OPERATOR)[number]

/** One recording's theme, as counted across churches (counts only: never which). */
export interface CatalogThemeVotes {
  /** The AI gave it to the recording. */
  ai: boolean
  /** Churches whose song (linked to the recording) has it added. */
  added: number
  /** Churches whose song has it removed. */
  removed: number
  /** Churches with the recording in their library (a song linked to it, matched or confirmed). */
  libraries: number
  operator: CatalogThemeOperator | null
}

/** Enough churches added it. */
export const addedByChurches = (v: Pick<CatalogThemeVotes, 'added'>) =>
  v.added >= CATALOG_THEME_ADD_CHURCHES

/**
 * Enough churches removed it: at least three, and at least half of the
 * churches that have the recording (a church that removed it has it, so the
 * libraries are never fewer than the removals).
 */
export function droppedByChurches(v: Pick<CatalogThemeVotes, 'removed' | 'libraries'>): boolean {
  const libraries = Math.max(v.libraries, v.removed)
  return (
    v.removed >= CATALOG_THEME_DROP_CHURCHES && v.removed >= libraries * CATALOG_THEME_DROP_SHARE
  )
}

/**
 * Whether the recording has the theme now. An operator's pin or block
 * decides; else the AI's themes and the ones enough churches added, unless
 * enough churches removed it. A pure function of the counts, so recounting
 * is idempotent and never flaps.
 */
export function catalogThemeActive(v: CatalogThemeVotes): boolean {
  if (v.operator === 'pinned') return true
  if (v.operator === 'blocked') return false
  return (v.ai || addedByChurches(v)) && !droppedByChurches(v)
}

export type CatalogThemeSource = 'pinned' | 'blocked' | 'ai' | 'churches' | 'dropped'

/** Where a recording's theme stands, for the platform console; null: not the catalog's. */
export function catalogThemeSource(v: CatalogThemeVotes): CatalogThemeSource | null {
  if (v.operator) return v.operator
  if ((v.ai || addedByChurches(v)) && droppedByChurches(v)) return 'dropped'
  if (v.ai) return 'ai'
  if (addedByChurches(v)) return 'churches'
  return null
}

export const CATALOG_THEME_SOURCE_LABELS: Record<CatalogThemeSource, string> = {
  pinned: 'Pinned by ForHim',
  blocked: 'Blocked by ForHim',
  ai: 'AI',
  churches: 'Added by churches',
  dropped: 'Dropped by churches',
}

// ---- clearing the statistics ----------------------------------------------------------------

/** A platform operator cleared the statistics: of one recording, or (null) of all. */
export interface StatsReset {
  catalogSongId: string | null
  at: Date
}

/**
 * The day after which a recording's services count again (YYYY-MM-DD, UTC),
 * from the latest reset that applies to it; null when none does.
 */
export function statsCountAfter(
  resets: readonly StatsReset[],
  catalogSongId: string,
): string | null {
  let latest: Date | null = null
  for (const reset of resets)
    if (reset.catalogSongId === null || reset.catalogSongId === catalogSongId)
      if (!latest || reset.at > latest) latest = reset.at
  return latest ? latest.toISOString().slice(0, 10) : null
}

/**
 * Does a service on this day count, given the resets? Only services after
 * the day it was cleared: clearing wipes what came before, the day itself
 * included.
 */
export function countsAfterReset(
  resets: readonly StatsReset[],
  catalogSongId: string,
  day: string,
): boolean {
  const after = statsCountAfter(resets, catalogSongId)
  return after === null || day > after
}

// ---- choosing a theme to add ----------------------------------------------------------------

/** A vocabulary theme offered while someone types (with the other word it was found by). */
export interface ThemeOption {
  slug: string
  label: string
  /** The synonym that matched ("Easter" for Resurrection); null when the label did. */
  via: string | null
}

/**
 * The vocabulary's themes for what was typed, not those in `exclude`: the
 * label starting with it first, then a word of the label starting with it,
 * then a synonym that does ("Easter" finds Resurrection). With nothing
 * typed, every theme in the vocabulary's order. At most `limit`.
 */
export function searchThemes(
  query: string,
  exclude: Iterable<string> = [],
  limit = 50,
): ThemeOption[] {
  const skip = new Set(exclude)
  const q = normaliseThemeLabel(query)
  const ranked: { option: ThemeOption; rank: number; index: number }[] = []
  for (const [index, entry] of THEMES.entries()) {
    if (skip.has(entry.slug)) continue
    if (!q) {
      ranked.push({ option: { slug: entry.slug, label: entry.label, via: null }, rank: 0, index })
      continue
    }
    const label = normaliseThemeLabel(entry.label)
    const wordStart = (words: string) =>
      words === q || words.startsWith(q) || words.includes(` ${q}`)
    if (label.startsWith(q))
      ranked.push({ option: { slug: entry.slug, label: entry.label, via: null }, rank: 0, index })
    else if (wordStart(label))
      ranked.push({ option: { slug: entry.slug, label: entry.label, via: null }, rank: 1, index })
    else {
      const synonym = entry.synonyms.find((s) => wordStart(normaliseThemeLabel(s)))
      if (synonym)
        ranked.push({
          option: { slug: entry.slug, label: entry.label, via: synonym },
          rank: 2,
          index,
        })
    }
  }
  return ranked
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map((r) => r.option)
}
